import { useMutation } from '@tanstack/react-query';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';
import { PutGpaCalculationsRequestBody, PutGpaCalculationsResponse } from '@ab/ams-school-api/types/private/v1';
import { GradePoint } from '~/types/transcriptExtraction';

type RecalculateGpaParams = {
  educationHistoryKey: string;
  gradePoints: GradePoint[];
  educationLevelCode?: string | null;
};

export function useRecalculateGpas(applicationId: string) {
  const request = useRequest();

  const { isLoading, mutateAsync } = useMutation({
    mutationKey: ['recalculateGpaCalculations', applicationId],
    mutationFn: async (params: RecalculateGpaParams) => {
      const response = await request<PutGpaCalculationsResponse>(
        `${config.apiHost}/private/v1/applications/${applicationId}/education_histories/${params.educationHistoryKey}/gpa_calculations`,
        {
          method: 'PUT',
          body: {
            data: {
              type: 'gpa_calculation_request',
              attributes: {
                gradePoints: params.gradePoints,
                educationLevelCode: params.educationLevelCode ?? null,
              },
            },
          } as PutGpaCalculationsRequestBody,
          isExpectedResponse,
        }
      );

      return response.data;
    },
  });

  return {
    recalculateGpa: mutateAsync,
    isRecalculatingGpa: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is PutGpaCalculationsResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
